import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Container,
  Card,
  CardBody,
  Button,
  Spinner,
  Alert,
  FormGroup,
  Label,
  Badge,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "reactstrap";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import featuresService from "../../services/api/features-service";
import {
  faArrowLeft,
  faTimes,
  faSave,
  faMapMarkerAlt,
  faCheck,
  faEdit,
  faExclamationTriangle,
  faInfoCircle
} from "@fortawesome/free-solid-svg-icons";
import { useHistory, useLocation } from "react-router-dom";

import adminGrievanceService from "../../services/api/grievance-service";
import ProjectContext from "../../ProjectContext";
import UserContext from "../../UserContext";

// --- HELPER: Compare arrays of cities ---
const haveCitiesChanged = (oldCities, newCities) => {
  if (oldCities.length !== newCities.length) return true;
  const oldSet = new Set(oldCities);
  for (let city of newCities) {
    if (!oldSet.has(city)) return true;
  }
  return false;
};

const stateSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "44px",
    borderRadius: "999px",
    backgroundColor: "#ffffff",
    borderColor: state.isFocused ? "#5e72e4" : "#d0d5dd",
    boxShadow: state.isFocused ? "0 0 0 1px rgba(94,114,228,0.35)" : "none",
    "&:hover": { borderColor: "#5e72e4" },
    fontSize: "0.9rem"
  }),
  valueContainer: (provided) => ({ ...provided, padding: "0 16px" }),
  singleValue: (provided) => ({ ...provided, color: "#111827", fontWeight: 500 }),
  placeholder: (provided) => ({ ...provided, color: "#9ca3af" }),
  menu: (provided) => ({ ...provided, zIndex: 9999, fontSize: "0.9rem" }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#5e72e4" : state.isFocused ? "#f6f9fc" : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#111827",
    cursor: "pointer"
  }),
  input: (provided) => ({ ...provided, color: "#111827" }),
  indicatorSeparator: () => ({ display: "none" })
};

const GrievanceCitySettings = () => {
  const project = useContext(ProjectContext);
  const rawUserCtx = useContext(UserContext);
  const history = useHistory();
  const location = useLocation();

  const projectId = project?.id;
  if (projectId === "") {
    history.push("/project/allprojects");
  }

  useEffect(() => {
    const checkFeature = async () => {
      if (projectId) {
        try {
          const featureStatus = await featuresService.getFeatureStatus(
            "enable_grievance_management",
            projectId
          );
          if (featureStatus?.data?.enable_grievance_management === false) {
            history.push("/dashboard/grievance-management");
          }
        } catch (error) {
          console.log("Error checking feature status", error);
        }
      }
    };
    checkFeature();
  }, [projectId, history]);

  const currentUser = rawUserCtx?.userdetails || rawUserCtx;
  const rolesFromProject = project?.current_user_roles || [];
  const rolesFromUser = currentUser?.current_user_roles || currentUser?.roles || [];
  const userRoles = rolesFromProject.length ? rolesFromProject : rolesFromUser;

  let accessLevel = "admin";
  if (userRoles.includes("admin") || userRoles.includes("owner")) {
    accessLevel = "admin";
  } else if (userRoles.includes("accounts")) {
    accessLevel = "accounts";
  } else if (userRoles.includes("editor")) {
    accessLevel = "editor";
  }

  const canManageCities = accessLevel === "admin";

  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isCityLoading, setIsCityLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [statesOptions, setStatesOptions] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [citiesForSelectedState, setCitiesForSelectedState] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [savedSelections, setSavedSelections] = useState([]);

  // Stores original data for comparison: Map<StateName, Array<Cities>>
  const [initialDataMap, setInitialDataMap] = useState(new Map());

  // --- MODAL STATE: 2 Counts Only ---
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveStats, setSaveStats] = useState({ deleted: 0, updated: 0 });

  const [alertState, setAlertState] = useState({ show: false, color: "", message: "" });

  const showAlert = (color, message) => {
    setAlertState({ show: true, color, message });
    setTimeout(() => setAlertState({ show: false, color: "", message: "" }), 3500);
  };

  // -------- INITIAL LOAD --------
  useEffect(() => {
    if (!projectId) return;
    if (!canManageCities) return;

    const init = async () => {
      setIsInitialLoading(true);
      try {
        const [statesRes, locationsRes] = await Promise.all([
          adminGrievanceService.getStates(projectId, "IN"),
          adminGrievanceService.getProjectLocations(projectId)
        ]);

        let opts = [];
        if (statesRes?.status) {
          opts = (statesRes.data || []).map((s) => ({
            label: s.name,
            value: s.isoCode
          }));
          setStatesOptions(opts);
        }

        if (locationsRes?.status) {
          const rawLocations = locationsRes.data?.locations || locationsRes.data || [];
          const byState = new Map();

          rawLocations.forEach((loc) => {
            if (!loc || !loc.state) return;
            const cities = loc.cities || loc.allowed_cities || [];
            const matchStateOpt = opts.find((o) => o.label === loc.state);
            const foundIso = loc.state_iso || matchStateOpt?.value || "";

            byState.set(loc.state, {
              state: loc.state,
              stateIso: foundIso,
              cities
            });
          });

          const merged = Array.from(byState.values());
          setSavedSelections(merged);

          // Store initial map for comparison
          const initialMap = new Map();
          merged.forEach(item => {
            initialMap.set(item.state, [...item.cities]);
          });
          setInitialDataMap(initialMap);
        }
      } catch (err) {
        console.log("Failed to load city settings", err);
        showAlert("danger", "Failed to load location settings.");
      } finally {
        setIsInitialLoading(false);
      }
    };

    init();
  }, [projectId, canManageCities]);

  // -------- MEMOIZED SORTING --------
  const sortedCityList = useMemo(() => {
    if (!citiesForSelectedState.length) return [];
    const selectedSet = new Set(selectedCities.map((c) => c.value));
    return [...citiesForSelectedState].sort((a, b) => {
      const isA = selectedSet.has(a.value);
      const isB = selectedSet.has(b.value);
      if (isA && !isB) return -1;
      if (!isA && isB) return 1;
      return a.label.localeCompare(b.label);
    });
  }, [citiesForSelectedState, selectedCities]);

  const handleBack = (e) => {
    if (e) e.preventDefault();
    if (location.state?.returnTab) {
      history.push("/dashboard/grievance-management", { activeTab: location.state.returnTab });
    } else {
      history.goBack();
    }
  };

  const handleStateChange = async (option) => {
    setSelectedState(option);
    setSelectedCities([]);
    setSelectAll(false);
    setCitiesForSelectedState([]);

    if (!option || !projectId || !canManageCities) return;

    setIsCityLoading(true);
    try {
      const res = await adminGrievanceService.getCitiesForState(projectId, option.value, "IN");
      if (res?.status) {
        const allCities = (res.data || []).map((c) => ({ label: c.name, value: c.name }));
        setCitiesForSelectedState(allCities);

        const existing = savedSelections.find((s) => s.state === option.label);
        if (existing && existing.cities && existing.cities.length > 0) {
          const preSelected = allCities.filter((c) => existing.cities.includes(c.label));
          setSelectedCities(preSelected);
          setSelectAll(preSelected.length === allCities.length && allCities.length > 0);
        }
      }
    } catch (err) {
      console.log("Failed to load cities for state", err);
      showAlert("danger", "Failed to load cities.");
    } finally {
      setIsCityLoading(false);
    }
  };

  const handleCityToggle = (cityObj) => {
    const exists = selectedCities.find((c) => c.value === cityObj.value);
    let next = [];
    if (exists) {
      next = selectedCities.filter((c) => c.value !== cityObj.value);
    } else {
      next = [...selectedCities, cityObj];
    }
    setSelectedCities(next);
    setSelectAll(next.length === citiesForSelectedState.length && citiesForSelectedState.length > 0);
  };

  const handleSelectAllToggle = () => {
    if (!citiesForSelectedState.length) return;
    if (selectAll) {
      setSelectedCities([]);
      setSelectAll(false);
    } else {
      setSelectedCities(citiesForSelectedState);
      setSelectAll(true);
    }
  };

  const handleAddSelection = () => {
    if (!selectedState) {
      showAlert("warning", "Please select a state first.");
      return;
    }
    if (!selectedCities.length) {
      showAlert("warning", "Please select at least one city.");
      return;
    }

    const newEntry = {
      state: selectedState.label,
      stateIso: selectedState.value,
      cities: selectedCities.map((c) => c.label)
    };

    setSavedSelections((prev) => {
      const filtered = prev.filter((s) => s.state !== newEntry.state);
      return [newEntry, ...filtered];
    });

    showAlert("success", `Updated ${selectedCities.length} ${selectedCities.length === 1 ? "city" : "cities"} for ${selectedState.label}.`);
  };

  const handleEditSelection = (selection) => {
    const stateOption = statesOptions.find((opt) => opt.label === selection.state);
    if (stateOption) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      handleStateChange(stateOption);
    } else {
      showAlert("danger", "State configuration not found in options.");
    }
  };

  const handleRemoveSelection = (index) => {
    setSavedSelections((prev) => prev.filter((_, i) => i !== index));
  };

  // --- CALCULATE CHANGES (DELETED vs UPDATED) ---
  const handleSaveClick = () => {
    if (!canManageCities) {
      showAlert("warning", "You do not have permission to update locations.");
      return;
    }
    if (!savedSelections.length && initialDataMap.size === 0) {
      showAlert("warning", "No locations to save.");
      return;
    }

    const currentStatesSet = new Set(savedSelections.map((s) => s.state));

    let deletedCount = 0;
    let updatedCount = 0;

    // 1. Calculate Deleted (Exists in Initial Map, but NOT in Current selections)
    for (let oldState of initialDataMap.keys()) {
      if (!currentStatesSet.has(oldState)) {
        deletedCount++;
      }
    }

    // 2. Calculate Updated (Includes NEW creations + MODIFICATIONS)
    savedSelections.forEach(sel => {
      // Case A: New Creation (Was not in initial map)
      if (!initialDataMap.has(sel.state)) {
        updatedCount++;
      }
      // Case B: Modification (Was in map, but cities changed)
      else {
        const oldCities = initialDataMap.get(sel.state);
        const newCities = sel.cities;
        if (haveCitiesChanged(oldCities, newCities)) {
          updatedCount++;
        }
      }
    });

    // If nothing changed at all
    if (deletedCount === 0 && updatedCount === 0) {
      showAlert("info", "No changes detected.");
      return;
    }

    setSaveStats({ deleted: deletedCount, updated: updatedCount });
    setSaveModalOpen(true);
  };

  // --- EXECUTE SAVE ---
  const executeSave = async () => {
    setSaveModalOpen(false);
    setIsSaving(true);
    try {
      const currentStates = new Set(savedSelections.map((s) => s.state));

      // 1. Handle Deletions
      for (let oldState of initialDataMap.keys()) {
        if (!currentStates.has(oldState)) {
          await adminGrievanceService.deleteProjectState(projectId, oldState);
        }
      }

      // 2. Handle Upserts (New + Modified)
      for (const sel of savedSelections) {
        let isoToSave = sel.stateIso;

        if (!isoToSave && statesOptions.length > 0) {
          const match = statesOptions.find((o) => o.label === sel.state);
          if (match) isoToSave = match.value;
        }

        if (!isoToSave) continue;

        // Optimization: Only save if New or Changed
        const isNew = !initialDataMap.has(sel.state);
        const isModified = isNew ? false : haveCitiesChanged(initialDataMap.get(sel.state), sel.cities);

        if (isNew || isModified) {
          const payload = {
            state: sel.state,
            state_iso: isoToSave,
            allowed_cities: sel.cities
          };
          await adminGrievanceService.saveProjectCities(projectId, payload);
        }
      }

      // Update Initial Map to reflect new state
      const newMap = new Map();
      savedSelections.forEach(item => {
        newMap.set(item.state, [...item.cities]);
      });
      setInitialDataMap(newMap);

      setSelectedState(null);
      setSelectedCities([]);
      setCitiesForSelectedState([]);
      setSelectAll(false);

      showAlert("success", "Location settings updated successfully.");
    } catch (err) {
      console.log("Failed to save all selections", err);
      showAlert("danger", "Failed to save location settings.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!canManageCities) {
    return (
      <div className="py-4" style={{ backgroundColor: "#f8f9fe", minHeight: "100vh" }}>
        <Container fluid>
          <Card className="shadow border-0 mt-5">
            <CardBody className="p-5 text-center">
              <h3 className="mb-3" style={{ color: "#111827" }}>Access Restricted</h3>
              <p className="text-muted mb-4">You do not have permission to manage grievance locations.</p>
              <Button color="primary" onClick={handleBack}>
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Back
              </Button>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="py-4" style={{ minHeight: "100vh" }}>
      <Container fluid style={{ maxWidth: 1200 }}>
        {/* Top bar */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h2 className="mb-1" style={{ color: "#111827", fontSize: "1.4rem" }}>Manage Cities</h2>
            <div className="text-muted small">Choose states and cities that are allowed for this project.</div>
          </div>
          <Button color="link" className="p-0 text-secondary" type="button" onClick={handleBack} style={{ fontSize: "0.85rem" }}>
            <FontAwesomeIcon icon={faArrowLeft} className="mr-1" style={{ marginRight: 5 }} /> Back to Grievance Management
          </Button>
        </div>

        {alertState.show && (
          <Alert color={alertState.color} className="mb-3 py-2 px-3">
            {alertState.message}
          </Alert>
        )}

        <Row className="align-items-start">
          <Col lg="6" className="mb-4">
            <Card className="shadow-sm border-0">
              <CardBody className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <Label className="mb-0 text-uppercase" style={{ fontWeight: 700, color: "#4b5563", fontSize: "0.85rem", letterSpacing: "0.08em" }}>Step 1 • Select State</Label>
                    <div className="small text-muted">Start by choosing the state you want to configure.</div>
                  </div>
                </div>

                {isInitialLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <div className="text-muted small mt-2">Loading states...</div>
                  </div>
                ) : (
                  <>
                    <FormGroup className="mb-4">
                      <Select
                        options={statesOptions}
                        value={selectedState}
                        onChange={handleStateChange}
                        placeholder="Choose a state..."
                        styles={stateSelectStyles}
                        isClearable
                        isDisabled={!statesOptions.length}
                      />
                    </FormGroup>

                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <Label className="mb-0 text-uppercase" style={{ fontWeight: 700, color: "#4b5563", fontSize: "0.85rem", letterSpacing: "0.08em" }}>Step 2 • Select Cities</Label>
                        <div className="small text-muted">Tap to toggle individual cities.</div>
                      </div>
                      <Button size="sm" color="secondary" outline className="px-3 py-1" onClick={handleSelectAllToggle} disabled={!citiesForSelectedState.length} style={{ borderRadius: "999px", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        {selectAll ? "Deselect All" : "Select All"}
                      </Button>
                    </div>

                    {selectedState && isCityLoading && (
                      <div className="d-flex align-items-center mt-2 mb-3">
                        <Spinner size="sm" color="primary" className="mr-2" /><span className="small text-muted">Loading cities…</span>
                      </div>
                    )}

                    {selectedState && !isCityLoading && (
                      <>
                        <div className="mb-3" style={{ display: "flex", flexWrap: "wrap", maxHeight: "260px", overflowY: sortedCityList.length > 20 ? "auto" : "visible" }}>
                          {sortedCityList.map((city) => {
                            const isSelected = !!selectedCities.find((c) => c.value === city.value);
                            return (
                              <button
                                key={city.value}
                                type="button"
                                onClick={(e) => { e.currentTarget.blur(); handleCityToggle(city); }}
                                className="btn btn-sm"
                                style={{
                                  borderRadius: "999px", marginRight: 8, marginBottom: 8, padding: "6px 14px",
                                  fontSize: "0.78rem", fontWeight: 500, borderWidth: 1, borderStyle: "solid",
                                  borderColor: isSelected ? "#5e72e4" : "#d0d5dd",
                                  backgroundColor: isSelected ? "#5e72e4" : "#ffffff",
                                  color: isSelected ? "#ffffff" : "#374151",
                                  display: "inline-flex", alignItems: "center", maxWidth: 220,
                                  whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden",
                                  boxShadow: isSelected ? "0 2px 6px rgba(94,114,228,0.35)" : "none",
                                  transition: "all 0.2s ease", outline: "none"
                                }}
                              >
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{city.label}</span>
                                {isSelected && <FontAwesomeIcon icon={faCheck} style={{ fontSize: "0.7rem", marginLeft: 6 }} />}
                              </button>
                            );
                          })}
                          {!sortedCityList.length && <div className="small text-muted">No cities found for this state.</div>}
                        </div>

                        {selectedCities.length > 0 && (
                          <Button color="primary" className="w-100 mt-1" style={{ borderRadius: "999px", fontWeight: 600, fontSize: "0.9rem" }} onClick={handleAddSelection}>
                            <FontAwesomeIcon icon={faCheck} className="mr-2" style={{ marginRight: 5 }} />
                            Update Selection ({selectedCities.length} {selectedCities.length === 1 ? "city" : "cities"})
                          </Button>
                        )}
                        {selectedCities.length === 0 && !isCityLoading && !!citiesForSelectedState.length && (
                          <div className="small text-muted mt-2">Click on one or more cities above, then press<span className="font-weight-bold"> Update Selection</span>.</div>
                        )}
                      </>
                    )}

                    {!selectedState && !isInitialLoading && <div className="text-muted small mt-1">Choose a state above to see its cities.</div>}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col lg="6" className="mb-4">
            <Card className="shadow-sm border-0">
              <CardBody className="p-4 d-flex flex-column">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h5 className="mb-1" style={{ color: "#111827", fontSize: "1rem" }}>Selected Locations</h5>
                    <div className="small text-muted">
                      {savedSelections.length > 0 ? (
                        <>{savedSelections.length} state{savedSelections.length > 1 ? "s" : ""} configured.</>
                      ) : (
                        <>No locations added yet.</>
                      )}
                    </div>
                  </div>
                  <Button
                    color="success"
                    className="px-4"
                    style={{ borderRadius: "999px", fontWeight: 600, fontSize: "0.85rem" }}
                    onClick={handleSaveClick}
                    disabled={isSaving}
                  >
                    {isSaving ? <><Spinner size="sm" className="mr-2" />Saving...</> : <><FontAwesomeIcon icon={faSave} className="mr-2" style={{ marginRight: 5 }} />Save All</>}
                  </Button>
                </div>

                {savedSelections.length === 0 ? (
                  <div className="flex-grow-1 d-flex align-items-center justify-content-center text-center" style={{ backgroundColor: "#f8fafc", borderRadius: "0.75rem", border: "1px dashed #cbd5e1", minHeight: '200px' }}>
                    <div className="p-3">
                      <div className="mb-2" style={{ fontSize: "1.8rem" }}>📍</div>
                      <div className="small text-muted">Select a state and its cities on the left, then click <strong>Update Selection</strong> to add it here.</div>
                    </div>
                  </div>
                ) : (
                  <div style={{ overflowY: "visible" }}>
                    {savedSelections.map((selection, index) => (
                      <Card key={`${selection.state}-${index}`} className="mb-3 border-0" style={{ backgroundColor: "#ffffff", borderRadius: "0.75rem", border: "1px solid #e5e7eb" }}>
                        <CardBody className="py-3 px-3">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="w-100">
                              <div className="d-flex align-items-center mb-2">
                                <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2" style={{ color: "#9ca3af", fontSize: "0.9rem", marginRight: "0.5rem" }} />
                                <h5 className="mb-0" style={{ color: "#111827", fontSize: "0.95rem", marginRight: "0.5rem" }}>{selection.state}</h5>
                                <span className="ml-2 badge badge-pill badge-light" style={{ fontSize: "0.7rem", color: "#4b5563" }}>
                                  {selection.cities.length} {selection.cities.length === 1 ? "city" : "cities"}
                                </span>
                              </div>
                              <div className="d-flex flex-wrap w-100">
                                {selection.cities.map((city) => (
                                  <Badge key={city} pill className="mr-2 mb-2" style={{ backgroundColor: "#e0f2fe", color: "#0f4c75", fontSize: "0.75rem", padding: "0.35rem 0.7rem", whiteSpace: 'normal' }}>
                                    {city}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            <div className="d-flex align-items-center flex-shrink-0 ml-2">
                              <Button color="link" className="text-info p-0 mr-3" onClick={() => handleEditSelection(selection)} style={{ fontSize: "0.8rem" }}>
                                <FontAwesomeIcon icon={faEdit} className="mr-1" style={{ marginRight: 5 }} />Edit
                              </Button>
                              <Button color="link" className="text-danger p-0" onClick={() => handleRemoveSelection(index)} style={{ fontSize: "0.8rem" }}>
                                <FontAwesomeIcon icon={faTimes} className="mr-1" style={{ marginRight: 5 }} />Remove
                              </Button>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* --- CONFIRMATION MODAL --- */}
      <Modal isOpen={saveModalOpen} toggle={() => setSaveModalOpen(false)} centered>
        <ModalBody className="py-4">
          <div className="text-center mb-4">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning mb-3" size="3x" />
            <h4 className="mb-1">Are you sure?</h4>
            <p className="text-muted">You are about to apply the following changes:</p>
          </div>

          <div className="d-flex justify-content-around px-3">
            <div className="text-center">
              <h3 className="text-danger font-weight-bold mb-0">{saveStats.deleted}</h3>
              <small className="text-uppercase text-muted font-weight-bold" style={{ fontSize: '0.7rem' }}>Deleted</small>
            </div>
            <div className="text-center">
              <h3 className="text-success font-weight-bold mb-0">{saveStats.updated}</h3>
              <small className="text-uppercase text-muted font-weight-bold" style={{ fontSize: '0.7rem' }}>Updated</small>
            </div>
          </div>

          {saveStats.deleted > 0 && (
            <Alert color="warning" className="mt-4 mb-0 small border-0" style={{ backgroundColor: '#857339ff' }}>
              <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
              <strong>Warning:</strong> Removing a state will immediately disable grievance submission for that location.
            </Alert>
          )}
        </ModalBody>
        <ModalFooter className="justify-content-center border-0 pb-4">
          <Button color="secondary" onClick={() => setSaveModalOpen(false)} className="px-4 rounded-pill">
            Cancel
          </Button>
          <Button color="success" onClick={executeSave} className="px-4 rounded-pill ml-3">
            Confirm Save
          </Button>
        </ModalFooter>
      </Modal>

    </div>
  );
};

export default GrievanceCitySettings;